type PlayerApiTarget = EventTarget & {
  play(): Promise<unknown> | unknown;
  pause?(): void;
  currentTime?: number;
  duration?: number;
  volume?: number;
  muted?: boolean;
  playbackRate?: number;
  loop?: boolean;
  paused?: boolean;
  ended?: boolean;
  dispatchEvent?(event: Event): boolean;
};

type NumericAccessor = {
  get(): number;
  set(value: number): void;
};

type BooleanAccessor = {
  get(): boolean;
  set(value: boolean): void;
};

export type BunnyPlayerApi = {
  play(): Promise<unknown> | unknown;
  pause(): void;
  on(event: string, callback: EventListenerOrEventListenerObject): void;
  off(event: string, callback: EventListenerOrEventListenerObject): void;
  trigger(event: string, data?: unknown): void;
  currentTime: NumericAccessor;
  volume: NumericAccessor;
  muted: BooleanAccessor;
  playbackRate(rate?: number): number;
  getPaused(): boolean;
  mute(): void;
  unmute(): void;
  getMuted(): boolean;
  setVolume(value: number): void;
  getVolume(): number;
  getDuration(): number;
  setCurrentTime(value: number): void;
  getCurrentTime(): number;
  setLoop(value: boolean): void;
  getLoop(): boolean;
  setPlaybackRate(value: number): void;
  getPlaybackRate(): number;
};

function getFiniteNumber(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function clampVolumePercent(value: number): number {
  return Math.min(Math.max(value, 0), 100);
}

function normalizePlaybackRate(value: number): number {
  return Number.isFinite(value) && value > 0 ? value : 1;
}

export function createBunnyPlayerApi(
  playerEl: PlayerApiTarget,
): BunnyPlayerApi {
  const api: BunnyPlayerApi = {
    play: () => playerEl.play(),
    pause: () => {
      playerEl.pause?.();
    },
    on: (event, callback) => {
      playerEl.addEventListener(event, callback);
    },
    off: (event, callback) => {
      playerEl.removeEventListener(event, callback);
    },
    trigger: (event, data) => {
      playerEl.dispatchEvent?.(new CustomEvent(event, { detail: data }));
    },
    currentTime: {
      get: () => api.getCurrentTime(),
      set: (value) => api.setCurrentTime(value),
    },
    volume: {
      get: () => getFiniteNumber(playerEl.volume, 1),
      set: (value) => api.setVolume(value * 100),
    },
    muted: {
      get: () => api.getMuted(),
      set: (value) => {
        if (value) {
          api.mute();
        } else {
          api.unmute();
        }
      },
    },
    playbackRate: (rate?: number) => {
      if (rate !== undefined) {
        api.setPlaybackRate(rate);
      }
      return api.getPlaybackRate();
    },
    getPaused: () => Boolean(playerEl.paused),
    mute: () => {
      playerEl.muted = true;
    },
    unmute: () => {
      playerEl.muted = false;
    },
    getMuted: () => Boolean(playerEl.muted),
    setVolume: (value) => {
      playerEl.volume = clampVolumePercent(value) / 100;
    },
    getVolume: () => Math.round(getFiniteNumber(playerEl.volume, 1) * 100),
    getDuration: () => getFiniteNumber(playerEl.duration, 0),
    setCurrentTime: (value) => {
      playerEl.currentTime = Math.max(getFiniteNumber(value, 0), 0);
    },
    getCurrentTime: () => Math.max(getFiniteNumber(playerEl.currentTime, 0), 0),
    setLoop: (value) => {
      playerEl.loop = Boolean(value);
    },
    getLoop: () => Boolean(playerEl.loop),
    setPlaybackRate: (value) => {
      playerEl.playbackRate = normalizePlaybackRate(value);
    },
    getPlaybackRate: () =>
      normalizePlaybackRate(getFiniteNumber(playerEl.playbackRate, 1)),
  };

  return api;
}

export type { PlayerApiTarget };
